import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Polygon,
  InfoWindow,
  MarkerF,
  PolylineF,
} from "@react-google-maps/api";
import { doc, setDoc } from "firebase/firestore";
import { db } from "firebaseDb";
import MDButton from "components/MDButton";
import { TextField, Stack } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import updateRowCoordinates from "./updateRows";
import rowData from "./PHL_B15.json";

// ✅ Export map to PDF
const exportMapToPDF = async () => {
  const mapElement = document.getElementById("map-container");
  const canvas = await html2canvas(mapElement, { useCORS: true, scale: 2 });
  const imgData = canvas.toDataURL("image/png");
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [canvas.width, canvas.height],
  });
  pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
  pdf.save("map.pdf");
};

// ✅ Generate distinct HSL colors
const generateColors = (count) =>
  Array.from({ length: count }, (_, i) => `hsl(${(i * 360) / count}, 70%, 60%)`);

export default function OrchardMap({
  currentOrchard,
  currentBlock,
  blockInfo,
  markerMode,
  newMarkerLabel,
  markerIcon,
  rowInfo,
}) {
  const mapRef = useRef(null);
  const polygonRefs = useRef({});
  const [areas, setAreas] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAreaName, setEditingAreaName] = useState("");
  const [editingCoordinates, setEditingCoordinates] = useState([]);
  console.log("rowInfo in OrchardMap:", rowInfo);

  // ✅ Google Maps loader
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyABtwgJZ_QsumquBtuTG71naVaKnW3E6LE",
  });

  useEffect(() => {
    const allSiteAreas = currentOrchard?.areas ? [...currentOrchard.areas] : [];
    allSiteAreas.sort((a, b) => {
      const numA = parseInt(a.area.match(/\d+/)?.[0] || a.id || 0, 10);
      const numB = parseInt(b.area.match(/\d+/)?.[0] || b.id || 0, 10);
      return numA - numB;
    });
    console.log("All Site Areas:", allSiteAreas);
    if (currentBlock) {
      const displayAreas = (currentBlock?.areas || []).filter((a) => !a.site);
      console.log("Display Areas for Block:", displayAreas);
      setAreas(displayAreas);
    } else {
      setAreas(allSiteAreas);
    }
  }, [blockInfo, currentBlock]);

  useEffect(() => {
    const source = currentBlock || currentOrchard;
    setMarkers(
      (source?.markers || []).map((marker) => ({ ...marker, icon: marker.icon || marker.iconUrl }))
    );
  }, [currentOrchard?.id, currentBlock?.id]);

  useEffect(() => {
    if (!mapRef.current || !areas?.length) return;

    const bounds = new window.google.maps.LatLngBounds();

    // Include all area coordinates in the bounds
    areas.forEach((area) => {
      area.coordinates?.forEach((coord) => {
        bounds.extend(coord);
      });
    });

    // Only fit bounds if there are valid coordinates
    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }
  }, [areas]);

  // ✅ Save areas/markers to Firestore
  const saveToFirestore = async (payload) => {
    if (!currentOrchard) return;
    const ref = currentBlock
      ? doc(db, "OrchardMaps", currentOrchard.id, "Blocks", currentBlock.id)
      : doc(db, "OrchardMaps", currentOrchard.id);
    await setDoc(ref, payload, { merge: true });
  };

  // ✅ Add new small square area at map center
  const handleAddArea = async () => {
    const name = prompt("Enter new area name:");
    if (!name) return;

    const center = mapRef.current?.getCenter();
    const lat = center?.lat() || -45;
    const lng = center?.lng() || 169;
    const offset = 0.0001;
    const squareCoords = [
      { lat: lat + offset, lng: lng - offset },
      { lat: lat + offset, lng: lng + offset },
      { lat: lat - offset, lng: lng + offset },
      { lat: lat - offset, lng: lng - offset },
    ];

    const newArea = {
      id: Date.now().toString(),
      area: name,
      coordinates: squareCoords,
      variety: "",
      extra: {},
      site: !currentBlock, // site area if orchard level
    };

    const updated = [...areas, newArea];
    setAreas(updated);
    await saveToFirestore({ areas: updated });
    mapRef.current?.panTo({ lat, lng });
  };

  // ✅ Add marker (orchard or block)
  const handleAddMarker = async (e) => {
    const iconUrl = markerIcon?.iconUrl || markerIcon?.url;
    const newMarker = {
      id: crypto.randomUUID(),
      createdAt: Date.now().toString(),
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
      label: newMarkerLabel,
      templateTitle: markerIcon?.title || markerIcon?.label || "",
      icon: iconUrl,
    };

    const updated = [...markers, newMarker];
    setMarkers(updated);
    await saveToFirestore({ markers: updated });
  };

  // ✅ Handle marker drag / delete
  const handleMarkerDragEnd = async (e, id) => {
    const updated = markers.map((m) =>
      m.id === id ? { ...m, lat: e.latLng.lat(), lng: e.latLng.lng() } : m
    );
    setMarkers(updated);
    await saveToFirestore({ markers: updated });
  };

  const handleDeleteMarker = async (id) => {
    const updated = markers.filter((m) => m.id !== id);
    setMarkers(updated);
    await saveToFirestore({ markers: updated });
  };

  // ✅ Save polygon edits
  const handleSaveArea = async () => {
    const updatedAreas = areas.map((a) =>
      a.id === selectedArea.id
        ? { ...a, area: editingAreaName, coordinates: editingCoordinates }
        : a
    );
    setAreas(updatedAreas);
    await saveToFirestore({ areas: updatedAreas });
    setIsEditing(false);
    setSelectedArea(null);
  };

  // ✅ Helpers
  const getPolygonTop = (path) => path.reduce((max, p) => (p.lat > max.lat ? p : max), path[0]);
  const uniqueNames = [...new Set(areas.map((a) => a.area))];
  const colors = useMemo(() => generateColors(uniqueNames.length), [uniqueNames.length]);
  const getColor = (name) => colors[uniqueNames.indexOf(name)] || "#777";

  if (!isLoaded) return <div>Loading map...</div>;
  if (!currentOrchard) return <div>Select an orchard first.</div>;

  return (
    <div>
      <MDButton onClick={exportMapToPDF}>Export Map to PDF</MDButton>
      <div id="map-container" style={{ width: "100%", height: "600px" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "600px" }}
          mapTypeId="satellite"
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onClick={(e) => markerMode && handleAddMarker(e)}
        >
          {/* Legend */}
          <div
            style={{
              position: "absolute",
              top: 55,
              left: 12,
              backgroundColor: "rgba(255,255,255,0.9)",
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "12px",
              zIndex: 1000,
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            <strong style={{ fontSize: "13px" }}>
              {currentBlock ? "Block Areas" : "Site Areas"}
            </strong>
            {uniqueNames.map((n, i) => (
              <div key={n} style={{ display: "flex", alignItems: "center", marginTop: 5 }}>
                <span
                  style={{
                    background: colors[i],
                    width: 16,
                    height: 10,
                    display: "inline-block",
                    marginRight: 6,
                    border: "1px solid rgba(0,0,0,0.2)",
                  }}
                />
                {n}
              </div>
            ))}
            <Stack>
              <MDButton
                variant="outlined"
                size="small"
                color="success"
                style={{ marginTop: 8, fontSize: 11 }}
                onClick={handleAddArea}
              >
                + Add Area
              </MDButton>
              <MDButton
                variant="outlined"
                size="small"
                color="secondary"
                style={{ marginTop: 8, fontSize: 11 }}
                onClick={() => {
                  updateRowCoordinates(rowData, currentOrchard?.id, currentBlock?.id);
                }}
              >
                + Add Rows
              </MDButton>
            </Stack>
          </div>

          {/* Polygons */}
          {!markerMode &&
            areas.map((area) => (
              <Polygon
                key={area.id}
                paths={
                  isEditing && selectedArea?.id === area.id ? editingCoordinates : area.coordinates
                }
                options={{
                  fillColor: getColor(area.area),
                  fillOpacity: 0.4,
                  strokeColor: "#333",
                  strokeWeight: 1,
                }}
                onClick={() => {
                  setSelectedArea(area);
                  setEditingAreaName(area.area);
                }}
                editable={isEditing && selectedArea?.id === area.id}
                onMouseUp={() => {
                  if (!isEditing || selectedArea?.id !== area.id) return;
                  const polygon = polygonRefs.current[area.id];
                  if (!polygon) return;
                  const path = polygon
                    .getPath()
                    .getArray()
                    .map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
                  setEditingCoordinates(path);
                }}
                onLoad={(poly) => {
                  polygonRefs.current[area.id] = poly;
                }}
              />
            ))}

          {/* Markers */}
          {markers.map((m) => (
            <MarkerF
              key={m.id}
              position={{ lat: m.lat, lng: m.lng }}
              label={{ text: m.label, color: "white" }}
              draggable
              icon={m.icon || "https://maps.google.com/mapfiles/ms/icons/red-dot.png"}
              onDragEnd={(e) => handleMarkerDragEnd(e, m.id)}
              onDblClick={() => markerMode && handleDeleteMarker(m.id)}
            />
          ))}

          {/* Rows */}
          {rowInfo?.map((row) => (
            <>
              <PolylineF
                path={row.coordinates}
                options={{
                  strokeColor: "#FF0000",
                  strokeWeight: 1,
                  clickable: true,
                }}
              />
              <MarkerF
                position={row.coordinates ? row.coordinates[1] : null} // top of row
                label={{
                  text: `${row.RowID}`,
                  fontSize: "6px",
                  color: "#ffffffff",
                  fontWeight: "bold",
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 0, // hide icon
                }}
              />
            </>
          ))}

          {/* Info Window */}
          {selectedArea && (
            <InfoWindow
              position={getPolygonTop(selectedArea.coordinates)}
              onCloseClick={() => {
                setSelectedArea(null);
                setIsEditing(false);
              }}
            >
              <div style={{ minWidth: 220, display: "flex", flexDirection: "column", gap: 8 }}>
                {!isEditing ? (
                  <>
                    <strong>Area:</strong> {selectedArea.area}
                    <MDButton
                      variant="contained"
                      size="small"
                      onClick={() => {
                        setIsEditing(true);
                        setEditingCoordinates(selectedArea.coordinates);
                      }}
                    >
                      Edit
                    </MDButton>
                  </>
                ) : (
                  <>
                    <TextField
                      label="Area Name"
                      variant="outlined"
                      size="small"
                      value={editingAreaName}
                      onChange={(e) => setEditingAreaName(e.target.value)}
                    />
                    <MDButton
                      variant="contained"
                      size="small"
                      color="success"
                      onClick={handleSaveArea}
                    >
                      Save
                    </MDButton>
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </div>
  );
}
